import React, {useState} from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import {format} from 'date-fns';
import DatabaseMetadataTableHead from './DatabaseMetadataTableHead';
import DatabaseMetadataTableToolbar from './DatabaseMetadataTableToolbar';
import {DatabaseMetadataEstadoUtils} from '../../utils/DatabaseMetadadaEstadoUtils';
import {DatabaseMetadataTableProps} from './DatabaseMetadataTableComponentsProps';
import {arreglos} from 'lib.common';
import DatabaseMetadataTableToolbarActions from './DatabaseMetadataTableToolbarActions';

const cellstyle = {
   overflow: 'hidden',
   textOverflow: 'ellipsis',
   whiteSpace: 'nowrap'
};

const DatabaseMetadataTable: React.FC<DatabaseMetadataTableProps> = ({
   data,
   estadoData,
   appSelected,
   onAppSelectedChange,
   selected,
   onSelectedChange,
   onDeleteClick,
   nroDias,
   onNroDiasChange
}) => {
   //#region >> toolbar actions
   //
   //
   //
   //
   //#region >>>> filtros
   const [estadosElegidos, setEstadosElegidos] = useState<string[]>([]);
   const filteredRows = React.useMemo(() => {
      if (!data || data.length === 0) {
         return [];
      }

      return data.filter(curr => {
         if (estadosElegidos.length === 0) {
            return true;
         }
         return estadosElegidos.includes(curr.estado);
      });
   }, [data, estadosElegidos]);
   //#endregion

   const ToolbarActions = (
      <DatabaseMetadataTableToolbarActions
         estadosElegidos={estadosElegidos}
         onEstadosElegidosChange={estados => {
            setEstadosElegidos(estados);
         }}
         appSelected={appSelected}
         onAppSelectedChange={onAppSelectedChange}
         nroDias={nroDias}
         onNroDiasChange={onNroDiasChange}
      />
   );
   //#endregion

   //#region >> select items

   const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newSelected = filteredRows.map(n => n.id);
      if (filteredRows && event.target.checked) {
         onSelectedChange(arreglos.mergeUnique(selected, newSelected));
         return;
      }
      onSelectedChange(arreglos.difference(selected, newSelected));
   };

   const handleClick = (event: React.MouseEvent<unknown>, id: string) => {
      const selectedIndex = selected.indexOf(id);
      let newSelected: string[] = [];

      if (selectedIndex === -1) {
         newSelected = newSelected.concat(selected, id);
      } else if (selectedIndex === 0) {
         newSelected = newSelected.concat(selected.slice(1));
      } else if (selectedIndex === selected.length - 1) {
         newSelected = newSelected.concat(selected.slice(0, -1));
      } else if (selectedIndex > 0) {
         newSelected = newSelected.concat(
            selected.slice(0, selectedIndex),
            selected.slice(selectedIndex + 1)
         );
      }
      onSelectedChange(newSelected);
   };

   const isSelected = (id: string) => selected.indexOf(id) !== -1;
   //#endregion

   //#region >> paginacion
   const [rowsPerPage, setRowsPerPage] = useState(10);
   const [page, setPage] = useState(0);

   const handleChangePage = (event: unknown, newPage: number) => {
      setPage(newPage);
   };

   const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
   };

   const visibleRows = React.useMemo(() => {
      if (!filteredRows || filteredRows.length === 0) {
         return [];
      }

      return filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
   }, [filteredRows, page, rowsPerPage]);
   //#endregion

   const rowCount = filteredRows ? filteredRows.length : 0;
   const currFilteredSelectedCount = filteredRows.filter(curr => selected.includes(curr.id)).length;

   return (
      <Paper sx={{width: '100%'}}>
         <DatabaseMetadataTableToolbar
            numSelected={selected.length}
            onDeleteClick={onDeleteClick}
            toolbarActionsComponent={ToolbarActions}
         />
         <TableContainer sx={{width: '100%', mb: 2}}>
            <Table
               sx={{width: '100%', tableLayout: 'fixed'}}
               size="small"
               aria-label="a dense table"
            >
               <DatabaseMetadataTableHead
                  numSelected={currFilteredSelectedCount}
                  onSelectAllClick={handleSelectAllClick}
                  rowCount={rowCount}
               />

               <TableBody>
                  {estadoData != null && (
                     <TableRow sx={{'&:last-child td, &:last-child th': {border: 0}}}>
                        <TableCell component="th" scope="row" colSpan={5}>
                           {estadoData}
                        </TableCell>
                     </TableRow>
                  )}
                  {estadoData == null &&
                     visibleRows &&
                     visibleRows.length > 0 &&
                     visibleRows.map(row => {
                        const isItemSelected = isSelected(row.id);
                        const labelId = `enhanced-table-checkbox-${row.id}`;

                        let cuentas: string | undefined = undefined;
                        if (row.cuentas && row.cuentas.length > 0) {
                           cuentas = `CUENTAS: ${row.cuentas.map(curr => curr.email).join(',')}`;
                        }
                        return (
                           <TableRow
                              hover
                              onClick={event => handleClick(event, row.id)}
                              role="checkbox"
                              aria-checked={isItemSelected}
                              tabIndex={-1}
                              key={row.id}
                              selected={isItemSelected}
                              sx={{cursor: 'pointer'}}
                           >
                              <TableCell padding="checkbox">
                                 <Checkbox
                                    color="primary"
                                    checked={isItemSelected}
                                    inputProps={{
                                       'aria-labelledby': labelId
                                    }}
                                 />
                              </TableCell>
                              <TableCell style={cellstyle}>{row.dbName}</TableCell>
                              <TableCell style={cellstyle}>{row.idInstancia}</TableCell>
                              <TableCell style={cellstyle} title={cuentas}>
                                 {row.denominacionInstancia}
                              </TableCell>
                              <TableCell style={cellstyle} align="right">
                                 {row.fechaExpiracion && format(row.fechaExpiracion, 'yyyy-mm-dd')}
                              </TableCell>
                              <TableCell
                                 sx={{
                                    backgroundColor: DatabaseMetadataEstadoUtils.getColor(
                                       row.estado
                                    ).main
                                 }}
                                 title={DatabaseMetadataEstadoUtils.getDescription(row.estado)}
                                 style={cellstyle}
                                 align="center"
                              >
                                 {row.estado}
                              </TableCell>
                           </TableRow>
                        );
                     })}
               </TableBody>
            </Table>
         </TableContainer>
         <TablePagination
            rowsPerPageOptions={[10]}
            component="div"
            count={rowCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
         />
      </Paper>
   );
};

export default DatabaseMetadataTable;
